import { FC, FormEvent, useState } from 'react';

import { toast } from 'react-toastify';

import {
  IRoutingConfigVersion,
  IVersionMetadataErrors,
  hasVersionMetadataErrors,
  isNameTaken,
  splitMetadataIssues,
  useEditVersionMetadata,
  validateVersionMetadata,
} from '@entities/routing/config-version';
import { useModal } from '@shared/components/Modal';

import VersionMetadataFields from './VersionMetadataFields';

interface IProps {
  version: IRoutingConfigVersion;
  // Other versions' names: the PATCH refuses a duplicate.
  takenNames: string[];
}

// Name and comment of a version on any status — metadata only, nothing goes live.
const EditVersionMetadataModal: FC<IProps> = ({ version, takenNames }) => {
  const { closeModal } = useModal();
  const { editVersionMetadata, isUpdating } = useEditVersionMetadata();
  const [name, setName] = useState(version.name);
  const [comment, setComment] = useState(version.comment ?? '');
  const [serverErrors, setServerErrors] = useState<IVersionMetadataErrors>({});
  const [error, setError] = useState<string | null>(null);

  // Only changed fields are sent (compared trimmed, like the body).
  const nameChanged = name.trim() !== version.name.trim();
  const commentChanged = comment.trim() !== (version.comment ?? '').trim();
  const isDirty = nameChanged || commentChanged;
  const isRenameBlocked = nameChanged && isNameTaken(name, takenNames);
  // Live: the fields start valid, so a message can only follow the operator's edit.
  const formErrors = validateVersionMetadata({ name, comment });
  const isFormInvalid = hasVersionMetadataErrors(formErrors);
  const shownErrors: IVersionMetadataErrors = { ...serverErrors, ...formErrors };

  // Typing into a field clears the server's message for it.
  const onNameChange = (value: string) => {
    setName(value);
    setServerErrors(({ name: _name, ...rest }) => rest);
    setError(null);
  };
  const onCommentChange = (value: string) => {
    setComment(value);
    setServerErrors(({ comment: _comment, ...rest }) => rest);
    setError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    // Enter in an input submits past the disabled button.
    if (isFormInvalid) return;
    setServerErrors({});
    setError(null);

    const result = await editVersionMetadata(version.id, {
      ...(nameChanged ? { name } : {}),
      ...(commentChanged ? { comment } : {}),
    });
    if (result.status === 'failed') {
      const { fields, general } = splitMetadataIssues(result.issues);
      setServerErrors(fields);
      setError(general);
      return;
    }

    toast.success(`v${version.id} details saved.`);
    closeModal();
  };

  return (
    <form className='routing-version-form' onSubmit={handleSubmit} noValidate>
      <h3 className='routing-version-form__title'>
        Edit details <span className='routing-version-number'>v{version.id}</span>
      </h3>
      <p className='routing-version-form__hint'>
        Only the label changes — v{version.id} keeps its status and its policies, and nothing needs
        publishing.
      </p>

      <VersionMetadataFields
        idPrefix='routing-version'
        name={name}
        comment={comment}
        onNameChange={onNameChange}
        onCommentChange={onCommentChange}
        takenNames={takenNames}
        duplicateNameHint='A version cannot be renamed to a name that is already in use.'
        errors={shownErrors}
        disabled={isUpdating}
      />

      {error && (
        <p className='routing-version-form__error routing-version-form__error--form' role='alert'>
          {error}
        </p>
      )}

      <div className='routing-version-form__btns'>
        <button type='button' className='secondary-btn' onClick={closeModal} disabled={isUpdating}>
          Cancel
        </button>
        <button type='submit' disabled={isUpdating || !isDirty || isRenameBlocked || isFormInvalid}>
          {isUpdating ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
};

export default EditVersionMetadataModal;
